import PromiseQueue from 'promise-queue';
import { fetchAccessTokenIfNeeded } from '~/AccessToken';
import { retryFetchAfterUserProductGroupLock } from '~/Api/User/ProductGroup';
import {
	ErrorResponse,
	findLtMaxPrice,
	LtMaxPriceResult,
	Update,
	updateLtMaxPrice as apiUpdateLtMaxPrice,
} from '~/Api/User/ProductGroup/LtMaxPrice';
import { invokeApi, resolve } from '~/ApiClient';
import { handleError } from '~/ErrorHandler';
import { flashErrorMessage } from '~/FlashMessage/actions';
import { fetchGroup } from '~/ProductGroup';
import { ThunkAction } from '~/Store';

import { Action, ActionType } from './Action';

const updateQueue = new PromiseQueue(1, Infinity);

export const fetchLtMaxPrice =
	(groupId: string): ThunkAction<Promise<LtMaxPriceResult>, Action> =>
	async (dispatch) => {
		const userId = (await dispatch(fetchAccessTokenIfNeeded())).accessToken.user.id;
		const request = dispatch(invokeApi(findLtMaxPrice, userId, groupId));
		dispatch({ type: ActionType.request, groupId, request });

		return resolve(request, (response) =>
			dispatch({
				groupId,
				ltMaxPrice: response?.ltMaxPrice ?? null,
				request,
				type: ActionType.receive,
			})
		);
	};

export const immediatelyUpdateLtMaxPrice =
	(groupId: string, update: Update): ThunkAction<Promise<void>, Action> =>
	async (dispatch) => {
		const userId = (await dispatch(fetchAccessTokenIfNeeded())).accessToken.user.id;
		const request = retryFetchAfterUserProductGroupLock(() =>
			dispatch(invokeApi(apiUpdateLtMaxPrice, userId, groupId, update))
		);
		dispatch({ type: ActionType.requestUpdate, groupId, request });

		await resolve(request, (response) =>
			dispatch({
				groupId,
				ltMaxPrice: response?.ltMaxPrice ?? null,
				request,
				type: ActionType.receiveUpdate,
			})
		);
	};

export const updateLtMaxPrice =
	(groupId: string, update: Update): ThunkAction<Promise<void>, Action> =>
	async (dispatch) => {
		const request = updateQueue.add(() => dispatch(immediatelyUpdateLtMaxPrice(groupId, update)));
		dispatch({ type: ActionType.requestUpdate, groupId, request });
		return request;
	};

export const handleLtMaxPriceUpdateResponse =
	<Response>(response: Response, groupId: string): ThunkAction<void, Action> =>
	(dispatch) => {
		if (!(response instanceof ErrorResponse)) {
			dispatch(handleError(response));
			return;
		}
		if (response.handleProductGroupNotFound()) {
			dispatch(fetchGroup(groupId));
		}
		if (response.handleCannotChangeSettingsForNonMemberProducts()) {
			dispatch(fetchLtMaxPrice(groupId)).catch(handleError);
		}
		if (response.handleIllegalPriceTypeSelected()) {
			dispatch(flashErrorMessage('Product reference prices has changed, please check selected product prices'));
			dispatch(fetchLtMaxPrice(groupId)).catch(handleError);
		}
		dispatch(handleError(response));
	};

export const pruneLtMaxPrice = (groupId: string): Action => ({
	type: ActionType.prune,
	groupId,
});

export const scheduleLtMaxPriceUpdate =
	(groupId: string, update: Update): ThunkAction<Promise<void>, Action> =>
	async (dispatch) =>
		new Promise((resolve) => {
			const timeoutId = setTimeout(() => {
				resolve(dispatch(updateLtMaxPrice(groupId, update)));
			}, 2000);

			dispatch({
				groupId,
				timeoutId,
				type: ActionType.scheduleUpdate,
				update,
			});
		});

export const commitLtMaxPriceUpdate =
	(groupId: string): ThunkAction<Promise<void>, Action> =>
	async (dispatch, getState) => {
		const { update } = getState().ltMaxPrice;
		if (update?.groupId !== groupId) {
			return;
		}
		if (update.state.name === 'fetching') {
			await update.state.request;
		}
		if (update.state.name === 'scheduled') {
			const { timeoutId, data } = update.state;
			clearTimeout(timeoutId);
			await dispatch(updateLtMaxPrice(groupId, data));
		}
	};

export const handleCommitError =
	<Error>(error: Error): ThunkAction<void, Action> =>
	(dispatch) => {
		if (!(error instanceof ErrorResponse)) {
			dispatch(handleError(error));
			return;
		}

		error.handleProductGroupNotFound();
		error.handleCannotChangeSettingsForNonMemberProducts();
		error.handleIllegalPriceTypeSelected();
		dispatch(handleError(error));
	};
